/**
 * Плашка «Пост в отложенных. Добавьте фото» — после «В отложенные» и из карточки запланированного поста.
 * Ключ сообщества фото не загружает: владелец скачивает их (уже с маскотом) и добавляет в отложенную запись во ВК.
 */
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, CircleCheck, Download, ExternalLink, List } from "lucide-react";
import type { Post, PostFull } from "@impuls/shared";
import { api } from "../lib/api";
import { humanDateTime } from "../lib/format";
import { EcgLoader } from "./Ecg";
import { Modal } from "./ui";

/** Плашка по уже загруженному посту (редактор). */
export function PostponedModal({ post, open, onClose }: { post: Post; open: boolean; onClose: () => void }) {
  const needPhotos = post.images.length > 0 && !post.photos_in_vk;
  return (
    <Modal open={open} onClose={onClose} title={needPhotos ? "Пост в отложенных. Добавьте фото" : "Пост в отложенных"} wide>
      <PostponedBody post={post} needPhotos={needPhotos} />
    </Modal>
  );
}

/** Плашка по id поста (календарь): пост подгружается, когда плашку открыли. */
export function PostponedModalById({ postId, open, onClose }: { postId: number; open: boolean; onClose: () => void }) {
  const { data: post, error } = useQuery({
    queryKey: ["post", postId],
    queryFn: () => api.get<PostFull>(`/api/posts/${postId}`),
    enabled: open,
  });
  if (post) return <PostponedModal post={post} open={open} onClose={onClose} />;
  return (
    <Modal open={open} onClose={onClose} title="Пост в отложенных">
      {error ? <p className="error-line">Пост не загрузился. Откройте его из календаря ещё раз.</p> : <EcgLoader label="Открываю пост" />}
    </Modal>
  );
}

function PostponedBody({ post, needPhotos }: { post: Post; needPhotos: boolean }) {
  const released = post.status !== "scheduled";
  return (
    <div className="pp">
      {post.scheduled_at && (
        <p className="pp__when">
          <CalendarClock size={18} aria-hidden="true" />
          {released ? "Вышел" : "Выйдет"} {humanDateTime(post.scheduled_at * 1000)}
        </p>
      )}

      {needPhotos && !released && (
        <ol className="pp__steps">
          <li>Откройте запись во ВК</li>
          <li>«Редактировать»</li>
          <li>Добавьте фото по порядку</li>
          <li>«Сохранить»</li>
        </ol>
      )}

      {needPhotos && (
        <section className="pp__photos" aria-label="Фото поста">
          <div className="pp__photos-head">
            <span className="pp__count">
              Фото: {post.images.length}
              <span className="muted"> · первое — главное</span>
            </span>
            <a className="btn btn--dark btn--small" href={`/api/posts/${post.id}/photos.zip`} download>
              <Download size={16} aria-hidden="true" />
              Скачать все фото
            </a>
          </div>
          <ol className="pp__thumbs">
            {post.images.map((img, i) => (
              <li key={img.id} className="pp__thumb">
                <img src={img.url} alt={`Фото ${i + 1}`} loading="lazy" />
                <span className="pp__num" aria-hidden="true">
                  {i + 1}
                </span>
                <a className="pp__dl" href={`/api/images/${img.id}/download`} download aria-label={`Скачать фото ${i + 1}`} title="Скачать">
                  <Download size={16} aria-hidden="true" />
                </a>
              </li>
            ))}
          </ol>
        </section>
      )}

      {post.photos_in_vk && post.images.length > 0 && (
        <p className="pp__ok">
          <CircleCheck size={18} aria-hidden="true" />
          Фото уже прикреплены к записи
        </p>
      )}

      <div className="pp__links">
        {post.vk_url && !released && (
          <a className="btn btn--primary" href={post.vk_url} target="_blank" rel="noreferrer">
            <ExternalLink size={17} aria-hidden="true" />
            Открыть пост во ВК
          </a>
        )}
        {post.vk_list_url && (
          <a className="btn btn--ghost" href={post.vk_list_url} target="_blank" rel="noreferrer">
            <List size={17} aria-hidden="true" />
            Все отложенные записи
          </a>
        )}
      </div>
    </div>
  );
}
